/**
 * @file crm-deal-move.ts
 * @description Перенос сделки между стадиями (phase19.31, зона «Клиенты и
 * сделки»): куда можно перенести, когда нужна причина и как объяснить отказ
 * сервера человеческими словами.
 *
 * Матрица переходов — зеркало серверной `crm/status-engine/transition-rules.ts`
 * (решает сервер; здесь — только чтобы не предлагать в списке заведомо
 * запрещённые стадии). Разойдутся — сервер откажет кодом
 * `stage_transition_forbidden`, и текст ниже это объяснит.
 */

import { ApiError } from "./api-client";
import type { DealLostReason, PipelineStageDto, StageKind } from "./api-crm";

/** Куда можно перейти из стадии данного вида (зеркало сервера). */
const ALLOWED_TRANSITIONS: Record<StageKind, StageKind[]> = {
  INITIAL: ["IN_PROGRESS", "LOST", "CANCELLED", "ON_HOLD"],
  IN_PROGRESS: ["IN_PROGRESS", "WON", "LOST", "CANCELLED", "ON_HOLD"],
  ON_HOLD: ["IN_PROGRESS", "LOST", "CANCELLED"],
  WON: [],
  LOST: ["IN_PROGRESS"],
  CANCELLED: [],
};

/** Стадии, переход в которые требует причины (`lost_reason_required`). */
const REASON_KINDS: ReadonlySet<StageKind> = new Set<StageKind>(["LOST", "CANCELLED"]);

/** Нужна ли причина при переносе в стадию этого вида. */
export function needsLostReason(kind: StageKind): boolean {
  return REASON_KINDS.has(kind);
}

/** Разрешён ли перенос между видами стадий. */
export function canMoveBetween(from: StageKind, to: StageKind): boolean {
  return ALLOWED_TRANSITIONS[from].includes(to);
}

/** Можно ли вообще перенести сделку из стадии этого вида (успешную и отменённую — нельзя). */
export function canLeaveStage(kind: StageKind): boolean {
  return ALLOWED_TRANSITIONS[kind].length > 0;
}

/** Стадии воронки, в которые сделку можно перенести (без текущей). */
export function moveTargets(stages: PipelineStageDto[], current: { id: string; kind: StageKind }): PipelineStageDto[] {
  return stages.filter((s) => s.isActive && s.id !== current.id && canMoveBetween(current.kind, s.kind));
}

/** Причины в порядке частоты; «Другое» — последней. */
export const DEAL_LOST_REASONS: DealLostReason[] = ["PRICE", "TIMING", "COMPETITOR", "NO_CONTACT", "PRODUCT_MISMATCH", "OTHER"];

/** Комментарий к причине — тот же предел, что у сервера. */
export const LOST_REASON_NOTE_MAX = 1000;

/** Отказ сервера словами менеджера (реестр по коду, не каскад if). */
const MOVE_ERROR_TEXT: Record<string, string> = {
  lost_reason_required: "Укажите причину — без неё сделку нельзя закрыть",
  stage_transition_forbidden: "В эту стадию из текущей перенести нельзя",
  stage_not_in_pipeline: "Стадия отключена или из другой воронки. Обновите страницу",
  deal_won_requires_customer: "Чтобы закрыть сделку успешно, укажите клиента",
  deal_won_requires_amount: "Чтобы закрыть сделку успешно, укажите сумму сделки",
  deal_not_found: "Сделка не найдена — возможно, её удалили",
};

export function dealMoveErrorText(err: unknown): string {
  if (err instanceof ApiError) return MOVE_ERROR_TEXT[err.code] ?? err.message;
  return "Не удалось перенести сделку. Попробуйте ещё раз";
}

/** Отказ из-за отсутствия причины — открыть окно причины. */
export function isLostReasonRequired(err: unknown): boolean {
  return err instanceof ApiError && err.code === "lost_reason_required";
}
